import { ActionHandler, ClearHandler } from './types';
import { logError } from '../../logger';
import type { ComponentController } from '../../services/AgentChatService';

/**
 * Creates a get value handler for a given component. It retrieves the current value from the component.
 */
const createGetValueHandler = <T>(
  getValueRef: React.RefObject<(() => T) | undefined>,
  componentId: string,
) => {
  if (!getValueRef.current) return;

  return () => {
    const handler = getValueRef.current;
    if (!handler) {
      logError(
        `Get value handler not available for component ${componentId}`,
        new Error(
          `Get value handler not available for component ${componentId}`,
        ),
      );
      return;
    }
    return handler();
  };
};

/**
 * Creates an action handler for a given component. It sets the AI generated values and calls the action handler.
 */
const createActionHandler = <T, V>(
  handlerRef: React.RefObject<ActionHandler<T, V> | undefined>,
  componentId: string,
  setAiGeneratedValues: (content: T | V) => void,
) => {
  if (!handlerRef.current) return;

  return (content: T | V) => {
    const handler = handlerRef.current;
    if (!handler) {
      logError(
        `Action handler not available for component ${componentId}`,
        new Error(`Action handler not available for component ${componentId}`),
      );
      return;
    }

    const result = handler(content);
    if (result) {
      // Extract the aiGeneratedValues from the result if returned
      const { aiGeneratedValues } = result;
      return setAiGeneratedValues(aiGeneratedValues);
    }

    return setAiGeneratedValues(content);
  };
};

/**
 * Creates a clear handler for a given component. It resets the AI generated values and calls the clear handler.
 */
const createClearHandler = (
  clearRef: React.RefObject<ClearHandler | undefined>,
  componentId: string,
  setAiGeneratedValues: (content: null) => void,
) => {
  if (!clearRef.current) return undefined;

  return () => {
    const handler = clearRef.current;
    if (!handler) {
      logError(
        `Clear handler not available for component ${componentId}`,
        new Error(`Clear handler not available for component ${componentId}`),
      );
      return;
    }

    handler();
    setAiGeneratedValues(null);
  };
};

const createControllerInstance = <T, V>(
  refs: {
    getValue: React.RefObject<(() => T | undefined) | undefined>;
    onReplace: React.RefObject<ActionHandler<T, V> | undefined>;
    onAdd: React.RefObject<ActionHandler<T, V> | undefined>;
    onRemove: React.RefObject<ActionHandler<T, V> | undefined>;
    onClear: React.RefObject<ClearHandler | undefined>;
  },
  componentId: string,
  setAiGeneratedValues: (content: T | V | null) => void,
): ComponentController<T, V> => ({
  getValue: createGetValueHandler(refs.getValue, componentId),
  onReplace: createActionHandler(
    refs.onReplace,
    componentId,
    setAiGeneratedValues,
  ),
  onAdd: createActionHandler(refs.onAdd, componentId, setAiGeneratedValues),
  onRemove: createActionHandler(
    refs.onRemove,
    componentId,
    setAiGeneratedValues,
  ),
  onClear: createClearHandler(refs.onClear, componentId, setAiGeneratedValues),
});

export { createControllerInstance };
